/**
 * Compiles the vendored OCPP 1.6 JSON Schemas with Ajv (draft-04) at module
 * load and exposes per-action validators.
 *
 * Lookup convention: requests live in `<Action>.json`, responses in
 * `<Action>Response.json`. The mobilityhouse mirror we vendored from uses
 * this split; the OCA's combined schemas in the official PDF release split
 * the same way once unpacked.
 */
import Ajv from 'ajv-draft-04';
import addFormats from 'ajv-formats';
import { relaxOcppUriFormats } from '@/protocol/ocppUriFormat';

// Eager-import every vendored schema so Vite resolves them at build time and
// they ship with the bundle (no fetch at runtime).
import authorizeReq from '@/../schemas/ocpp16/Authorize.json';
import authorizeRes from '@/../schemas/ocpp16/AuthorizeResponse.json';
import bootReq from '@/../schemas/ocpp16/BootNotification.json';
import bootRes from '@/../schemas/ocpp16/BootNotificationResponse.json';
import cancelReservationReq from '@/../schemas/ocpp16/CancelReservation.json';
import cancelReservationRes from '@/../schemas/ocpp16/CancelReservationResponse.json';
import changeAvailabilityReq from '@/../schemas/ocpp16/ChangeAvailability.json';
import changeAvailabilityRes from '@/../schemas/ocpp16/ChangeAvailabilityResponse.json';
import changeConfigurationReq from '@/../schemas/ocpp16/ChangeConfiguration.json';
import changeConfigurationRes from '@/../schemas/ocpp16/ChangeConfigurationResponse.json';
import clearCacheReq from '@/../schemas/ocpp16/ClearCache.json';
import clearCacheRes from '@/../schemas/ocpp16/ClearCacheResponse.json';
import clearChargingProfileReq from '@/../schemas/ocpp16/ClearChargingProfile.json';
import clearChargingProfileRes from '@/../schemas/ocpp16/ClearChargingProfileResponse.json';
import dataTransferReq from '@/../schemas/ocpp16/DataTransfer.json';
import dataTransferRes from '@/../schemas/ocpp16/DataTransferResponse.json';
import diagnosticsStatusNotificationReq from '@/../schemas/ocpp16/DiagnosticsStatusNotification.json';
import diagnosticsStatusNotificationRes from '@/../schemas/ocpp16/DiagnosticsStatusNotificationResponse.json';
import firmwareStatusNotificationReq from '@/../schemas/ocpp16/FirmwareStatusNotification.json';
import firmwareStatusNotificationRes from '@/../schemas/ocpp16/FirmwareStatusNotificationResponse.json';
import getCompositeScheduleReq from '@/../schemas/ocpp16/GetCompositeSchedule.json';
import getCompositeScheduleRes from '@/../schemas/ocpp16/GetCompositeScheduleResponse.json';
import getConfigurationReq from '@/../schemas/ocpp16/GetConfiguration.json';
import getConfigurationRes from '@/../schemas/ocpp16/GetConfigurationResponse.json';
import getDiagnosticsReq from '@/../schemas/ocpp16/GetDiagnostics.json';
import getDiagnosticsRes from '@/../schemas/ocpp16/GetDiagnosticsResponse.json';
import getLocalListVersionReq from '@/../schemas/ocpp16/GetLocalListVersion.json';
import getLocalListVersionRes from '@/../schemas/ocpp16/GetLocalListVersionResponse.json';
import heartbeatReq from '@/../schemas/ocpp16/Heartbeat.json';
import heartbeatRes from '@/../schemas/ocpp16/HeartbeatResponse.json';
import meterValuesReq from '@/../schemas/ocpp16/MeterValues.json';
import meterValuesRes from '@/../schemas/ocpp16/MeterValuesResponse.json';
import remoteStartTransactionReq from '@/../schemas/ocpp16/RemoteStartTransaction.json';
import remoteStartTransactionRes from '@/../schemas/ocpp16/RemoteStartTransactionResponse.json';
import remoteStopTransactionReq from '@/../schemas/ocpp16/RemoteStopTransaction.json';
import remoteStopTransactionRes from '@/../schemas/ocpp16/RemoteStopTransactionResponse.json';
import reserveNowReq from '@/../schemas/ocpp16/ReserveNow.json';
import reserveNowRes from '@/../schemas/ocpp16/ReserveNowResponse.json';
import resetReq from '@/../schemas/ocpp16/Reset.json';
import resetRes from '@/../schemas/ocpp16/ResetResponse.json';
import sendLocalListReq from '@/../schemas/ocpp16/SendLocalList.json';
import sendLocalListRes from '@/../schemas/ocpp16/SendLocalListResponse.json';
import setChargingProfileReq from '@/../schemas/ocpp16/SetChargingProfile.json';
import setChargingProfileRes from '@/../schemas/ocpp16/SetChargingProfileResponse.json';
import startTransactionReq from '@/../schemas/ocpp16/StartTransaction.json';
import startTransactionRes from '@/../schemas/ocpp16/StartTransactionResponse.json';
import statusNotificationReq from '@/../schemas/ocpp16/StatusNotification.json';
import statusNotificationRes from '@/../schemas/ocpp16/StatusNotificationResponse.json';
import stopTransactionReq from '@/../schemas/ocpp16/StopTransaction.json';
import stopTransactionRes from '@/../schemas/ocpp16/StopTransactionResponse.json';
import triggerMessageReq from '@/../schemas/ocpp16/TriggerMessage.json';
import triggerMessageRes from '@/../schemas/ocpp16/TriggerMessageResponse.json';
import unlockConnectorReq from '@/../schemas/ocpp16/UnlockConnector.json';
import unlockConnectorRes from '@/../schemas/ocpp16/UnlockConnectorResponse.json';
import updateFirmwareReq from '@/../schemas/ocpp16/UpdateFirmware.json';
import updateFirmwareRes from '@/../schemas/ocpp16/UpdateFirmwareResponse.json';

export type Direction = 'request' | 'response';
export type Validator = (data: unknown) => { valid: boolean; errors: readonly string[] };

const ajv = new Ajv({ allErrors: true, strict: false });
addFormats(ajv);
relaxOcppUriFormats(ajv);

const SCHEMAS: Record<string, { request: unknown; response: unknown }> = {
  Authorize: { request: authorizeReq, response: authorizeRes },
  BootNotification: { request: bootReq, response: bootRes },
  CancelReservation: { request: cancelReservationReq, response: cancelReservationRes },
  ChangeAvailability: { request: changeAvailabilityReq, response: changeAvailabilityRes },
  ChangeConfiguration: { request: changeConfigurationReq, response: changeConfigurationRes },
  ClearCache: { request: clearCacheReq, response: clearCacheRes },
  ClearChargingProfile: { request: clearChargingProfileReq, response: clearChargingProfileRes },
  DataTransfer: { request: dataTransferReq, response: dataTransferRes },
  DiagnosticsStatusNotification: {
    request: diagnosticsStatusNotificationReq,
    response: diagnosticsStatusNotificationRes,
  },
  FirmwareStatusNotification: {
    request: firmwareStatusNotificationReq,
    response: firmwareStatusNotificationRes,
  },
  GetCompositeSchedule: { request: getCompositeScheduleReq, response: getCompositeScheduleRes },
  GetConfiguration: { request: getConfigurationReq, response: getConfigurationRes },
  GetDiagnostics: { request: getDiagnosticsReq, response: getDiagnosticsRes },
  GetLocalListVersion: { request: getLocalListVersionReq, response: getLocalListVersionRes },
  Heartbeat: { request: heartbeatReq, response: heartbeatRes },
  MeterValues: { request: meterValuesReq, response: meterValuesRes },
  RemoteStartTransaction: {
    request: remoteStartTransactionReq,
    response: remoteStartTransactionRes,
  },
  RemoteStopTransaction: {
    request: remoteStopTransactionReq,
    response: remoteStopTransactionRes,
  },
  ReserveNow: { request: reserveNowReq, response: reserveNowRes },
  Reset: { request: resetReq, response: resetRes },
  SendLocalList: { request: sendLocalListReq, response: sendLocalListRes },
  SetChargingProfile: { request: setChargingProfileReq, response: setChargingProfileRes },
  StartTransaction: { request: startTransactionReq, response: startTransactionRes },
  StatusNotification: { request: statusNotificationReq, response: statusNotificationRes },
  StopTransaction: { request: stopTransactionReq, response: stopTransactionRes },
  TriggerMessage: { request: triggerMessageReq, response: triggerMessageRes },
  UnlockConnector: { request: unlockConnectorReq, response: unlockConnectorRes },
  UpdateFirmware: { request: updateFirmwareReq, response: updateFirmwareRes },
};

const VALIDATORS = new Map<string, Validator>();

function keyFor(action: string, direction: Direction): string {
  return `${action}|${direction}`;
}

function compile(action: string, direction: Direction): Validator {
  const schemaSet = SCHEMAS[action];
  if (!schemaSet) {
    return () => ({ valid: false, errors: [`unknown action "${action}"`] });
  }
  const schema = schemaSet[direction];
  const compiled = ajv.compile(schema as Record<string, unknown>);
  return (data) => {
    const ok = compiled(data);
    if (ok) return { valid: true, errors: [] };
    const errors = (compiled.errors ?? []).map(
      (e) => `${e.instancePath || '<root>'} ${e.message ?? 'unknown'}`,
    );
    return { valid: false, errors };
  };
}

export function validatorFor(action: string, direction: Direction): Validator {
  const k = keyFor(action, direction);
  let v = VALIDATORS.get(k);
  if (!v) {
    v = compile(action, direction);
    VALIDATORS.set(k, v);
  }
  return v;
}

export function listActions(): readonly string[] {
  return Object.keys(SCHEMAS);
}
